import { createSlice } from "@reduxjs/toolkit";
import { ILinkBankAccountForm } from "../../Pages/LinkBankAccount/LinkBankAccountForm";
import api from "../../Services/ApiServices";
import { IMICR, IStage6Info } from "../../Types/AccountOpeningRequest";
import AuthInfoContext from "../../Types/AuthInfoContext";
import { IOTPSession } from "../../Types/OTPSession";
import { dispatch, RootState } from "../store";
import { setAuthInfo, setStage } from "./common";
import Utils from "../../Services/Utils";
import Enums, { RW_PROCESS_BASIC_STATUS } from "../../enums";
import { tryEnqueueSnackbar } from "../../Main/Main";
import constants from "../../constants";

function transformMICRRecords(lstResponseMICR: any) {
  return Array.isArray(lstResponseMICR)
    ? lstResponseMICR.map((item) => {
        return {
          BankMICR: item.BankMICRCode,
          BankBranchAddress1: item.BankBranchAddress,
          ...item,
        };
      })
    : undefined;
}
export const DEFAULT_MICR = [
  {
    BankMICR: constants.MICR_NOT_LISTED,
  },
];

interface IStep6State {
  isLoading?: boolean;
  error?: any;
  objStage6Info?: IStage6Info;

  isLoadingIFSC?: boolean;
  errorIFSC?: any;
  MICRList?: IMICR[];
  CorrectedIFSCCode?: string;

  submitDetailLoading?: boolean;
  submitDetailError?: any;
  submitDetail?: IOTPSession;
  beneficiaryName?: string;
  isMockupSuccess: boolean;

  // Poling Request
  polingPercent: number;
  showPercent: boolean;
}

const initialState: IStep6State = {
  beneficiaryName: "",
  isMockupSuccess: true,

  polingPercent: 0,
  showPercent: false,
};

const slice = createSlice({
  name: "stage6",
  initialState,
  reducers: {
    //loading
    startLoading(state) {
      state.isLoading = true;
      state.error = undefined;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getStage6InfoSuccess(state, action) {
      state.isLoading = false;
      state.objStage6Info = action.payload;
      if (Array.isArray(action.payload?.lstBankMICRAndOtherInfo)) {
        state.MICRList = transformMICRRecords(
          action.payload?.lstBankMICRAndOtherInfo
        );
      }
      state.beneficiaryName = action.payload?.ClientFullNameAsPerPAN;
    },

    //ifsc
    setLoadingIFSC(state, action) {
      state.isLoadingIFSC = action.payload;
    },
    setIFSCError(state, action) {
      state.isLoadingIFSC = false;
      state.errorIFSC = action.payload;
    },
    IFSCSuccess(state, action) {
      state.isLoadingIFSC = false;
      state.MICRList = action.payload.Records;
      if (action.payload.Records1 && action.payload.Records1[0]) {
        state.CorrectedIFSCCode = action.payload.Records1[0].CorrectedIFSCCode;
      } else state.CorrectedIFSCCode = undefined;
    },

    submitDetailStartLoading(state) {
      state.submitDetailLoading = true;
      state.submitDetailError = undefined;
    },
    submitDetailHasError(state, action) {
      state.submitDetailLoading = false;
      state.submitDetailError = action.payload;
    },
    submitDetailSuccess(state, action) {
      state.submitDetail = action.payload;
      state.submitDetailLoading = false;
    },
    setBeneficiaryName(state, action) {
      state.beneficiaryName = action.payload;
    },
    setMockupSuccess(state, action) {
      state.isMockupSuccess = action.payload;
    },
    setPolingPercent(state, action) {
      state.polingPercent = action.payload;
    },
    setShowPoling(state, action) {
      state.showPercent = action.payload;
    },
    resetFields() {
      return { ...initialState };
    },
  },
});

// Reducer
export default slice.reducer;
export const {
  setLoadingIFSC,
  setMockupSuccess,
  setBeneficiaryName,
  resetFields,
} = slice.actions;

export const GetStage6Info = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.startLoading());

      const { objAuthInfoResponse } = getState().common;
      let response = await api.GetStage6Info({
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });

      // Throw error when status was false.
      if (!response.Status) throw new Error(response.Message);

      await dispatch(slice.actions.getStage6InfoSuccess(response));
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.hasError(ex));
    }
  };
};

export const getMICRList = (IFSC: string) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      dispatch(setLoadingIFSC(true));
      const { objAuthInfoResponse } = getState().common;
      let response = await api.GetMICRList({
        AOSAI: JSON.stringify(objAuthInfoResponse),
        IFSC: IFSC,
      });
      await dispatch(slice.actions.IFSCSuccess(response));
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.setIFSCError(ex));
    }
  };
};

export const SubmitBankAccountDetail = (values: ILinkBankAccountForm) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.submitDetailStartLoading());
      const { objAuthInfoResponse } = getState().common;
      const { objStage6Info, beneficiaryName, isMockupSuccess } =
        getState().stage6;
      let response = await api.SubmitBankAccountDetail({
        AOSAI: JSON.stringify(objAuthInfoResponse),
        CBAN: values.bankAccountNumber,
        CBIFSC: values.branchIFSC,
        CBMICR:
          values.branchMICR === constants.MICR_NOT_LISTED
            ? values.customMICR
            : values.branchMICR,
        CBATIC: values.bankAccountType,
        MIS: objStage6Info?.IsDevelopmentMode
          ? isMockupSuccess
            ? "1"
            : "0"
          : undefined,
        MBN: objStage6Info?.IsDevelopmentMode ? beneficiaryName : undefined,
      });
      // await dispatch(setStage(7)); // Temp

      const objAuthContext = new AuthInfoContext(
        response.ContextSystemObjectIDOrCode
      );
      if (!objAuthContext.Status) throw new Error(objAuthContext.Message);

      if (response.MemoryBag === true) {
        await dispatch(slice.actions.setShowPoling(true));

        // Reponse will only come when there will be success. otherwise it would have been thrown already.
        let statusResponse = await checkBankStatus(
          JSON.stringify(objAuthInfoResponse),
          (percent) => {
            dispatch(slice.actions.setPolingPercent(percent));
          }
        );
        await dispatch(slice.actions.setShowPoling(false));
        if (
          statusResponse.BankDetailsVerificationUsingPennyDropRWPBSInternalCode ===
          RW_PROCESS_BASIC_STATUS.SUCCESS
        )
          tryEnqueueSnackbar(statusResponse.Message, { variant: "success" });
        else if (
          [RW_PROCESS_BASIC_STATUS.WARNING, RW_PROCESS_BASIC_STATUS.FAIL]
        )
          tryEnqueueSnackbar(statusResponse.Message, { variant: "warning" });
        else throw new Error(statusResponse.Message); // On other status throw error.
      } else if (objAuthContext.Message) {
        tryEnqueueSnackbar(objAuthContext.Message, { variant: "success" });
      }

      await dispatch(slice.actions.submitDetailSuccess(response));
      await dispatch(setAuthInfo(response));
      await dispatch(setStage(objAuthContext.NextStage));
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.submitDetailHasError(ex));
      await dispatch(slice.actions.setShowPoling(false));
    }
  };
};

async function checkBankStatus(
  AOSAI: string,
  onProgress?: (percent: number) => void
) {
  let maxAttempt = 7;
  let interval = 5000;
  let lastEx = null;
  let lastResponse = null;
  for (let i = 0; i < maxAttempt; i++) {
    try {
      if (onProgress) onProgress((i + 1) * (100 / maxAttempt));
      lastResponse = await api.CheckBankDetailStatus({ AOSAI, PN: i + 1 });
      if (
        ![
          Enums.RW_PROCESS_BASIC_STATUS.NONE,
          Enums.RW_PROCESS_BASIC_STATUS.PENDING,
        ].includes(
          lastResponse.BankDetailsVerificationUsingPennyDropRWPBSInternalCode
        )
      )
        break;
    } catch (ex) {
      lastEx = ex;
    }
    await Utils.setTimeout(interval);
  }

  if (!lastResponse) throw lastEx;
  return lastResponse;
}
